import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { AdminPanel } from "../../components/admin/AdminPanel";
import { AdminTable } from "../../components/admin/AdminTable";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { useTenantDetail } from "../../hooks/useTenantDetail";
import { formatDateTime, statusTone, titleCase } from "../../lib/format";
import { tenantService } from "../../services/tenantService";
import type { TenantPlan, TenantStatus } from "../../types/admin";

const STATUSES: TenantStatus[] = ["trial", "active", "past_due", "cancelled", "suspended"];
const PLANS: TenantPlan[] = ["starter", "pro", "studio", "enterprise"];

export function AdminTenantDetailPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, refresh } = useTenantDetail(tenantId);
  const [status, setStatus] = useState<TenantStatus | "">("");
  const [plan, setPlan] = useState<TenantPlan | "">("");
  const [convertPlan, setConvertPlan] = useState<TenantPlan>("enterprise");
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function updateTenant() {
    if (!tenantId) return;
    setSaving(true);
    setActionError(null);
    try {
      await tenantService.updateTenant({
        tenantId,
        status: status || null,
        plan: plan || null,
      });
      setStatus("");
      setPlan("");
      await refresh();
    } catch (caughtError) {
      setActionError(caughtError instanceof Error ? caughtError.message : "Unable to update tenant.");
    } finally {
      setSaving(false);
    }
  }

  async function disableMember(membershipId: string) {
    setActionError(null);
    try {
      await tenantService.updateMembership({ membershipId, status: "disabled" });
      await refresh();
    } catch (caughtError) {
      setActionError(caughtError instanceof Error ? caughtError.message : "Unable to disable member.");
    }
  }

  async function extendTrial(days: number) {
    if (!tenantId) return;
    setSaving(true);
    setActionError(null);
    try {
      await tenantService.extendTrial(tenantId, days);
      await refresh();
    } catch (caughtError) {
      setActionError(caughtError instanceof Error ? caughtError.message : "Unable to extend trial.");
    } finally {
      setSaving(false);
    }
  }

  async function convertTrial() {
    if (!tenantId) return;
    setSaving(true);
    setActionError(null);
    try {
      await tenantService.convertTrial(tenantId, convertPlan);
      await refresh();
    } catch (caughtError) {
      setActionError(caughtError instanceof Error ? caughtError.message : "Unable to convert trial.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteTenant() {
    if (!tenantId || !data?.tenant) return;
    setActionError(null);
    const confirmation = window.prompt(
      `Type ${data.tenant.slug} to remove this tenant and its tenant-owned records.`,
    );

    if (!confirmation) return;

    setSaving(true);
    try {
      await tenantService.deleteTenant(tenantId, confirmation);
      navigate("/admin/tenants", { replace: true });
    } catch (caughtError) {
      setActionError(caughtError instanceof Error ? caughtError.message : "Unable to delete tenant.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <FullPageLoader label="Loading tenant" />;
  if (error) return <EmptyState title="Unable to load tenant.">{error}</EmptyState>;
  if (!data?.tenant) return <EmptyState title="Tenant not found." />;

  return (
    <div>
      <AdminPageHeader
        title={data.tenant.name}
        description="Tenant details, members, invites, feature flags, audit activity and status controls."
      />

      {actionError ? (
        <div className="mb-5 rounded-md border border-red-900/20 bg-red-50 p-4 text-sm text-red-900">
          {actionError}
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          <AdminPanel title="Members">
            <AdminTable headers={["Name", "Email", "Role", "Status", "Joined", "Actions"]}>
              {data.members.map((member) => (
                <tr key={member.id}>
                  <td className="px-4 py-3">{member.profiles?.full_name || "Unnamed"}</td>
                  <td className="px-4 py-3">{member.profiles?.email}</td>
                  <td className="px-4 py-3">{titleCase(member.role)}</td>
                  <td className="px-4 py-3">
                    <Badge tone={statusTone(member.status)}>{member.status}</Badge>
                  </td>
                  <td className="px-4 py-3">{formatDateTime(member.created_at)}</td>
                  <td className="px-4 py-3">
                    {member.status === "active" ? (
                      <Button onClick={() => void disableMember(member.id)} variant="secondary">
                        Disable
                      </Button>
                    ) : (
                      <span className="text-graphite/50">No action</span>
                    )}
                  </td>
                </tr>
              ))}
            </AdminTable>
          </AdminPanel>

          <AdminPanel title="Invites">
            <AdminTable headers={["Email", "Role", "Status", "Expires", "Created"]}>
              {data.invites.map((invite) => (
                <tr key={invite.id}>
                  <td className="px-4 py-3">{invite.invited_email}</td>
                  <td className="px-4 py-3">{titleCase(invite.role)}</td>
                  <td className="px-4 py-3">
                    <Badge tone={statusTone(invite.status)}>{invite.status}</Badge>
                  </td>
                  <td className="px-4 py-3">{formatDateTime(invite.expires_at)}</td>
                  <td className="px-4 py-3">{formatDateTime(invite.created_at)}</td>
                </tr>
              ))}
            </AdminTable>
          </AdminPanel>

          <AdminPanel title="Audit log entries">
            <AdminTable headers={["Action", "Entity", "Created"]}>
              {data.auditLogs.map((log) => (
                <tr key={log.id}>
                  <td className="px-4 py-3">{titleCase(log.action)}</td>
                  <td className="px-4 py-3">{log.entity_type ?? "System"}</td>
                  <td className="px-4 py-3">{formatDateTime(log.created_at)}</td>
                </tr>
              ))}
            </AdminTable>
          </AdminPanel>
        </div>

        <div className="space-y-6">
          <AdminPanel title="Tenant controls">
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-graphite/70">Current status</p>
                <div className="mt-2">
                  <Badge tone={statusTone(data.tenant.status)}>{data.tenant.status}</Badge>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-graphite/70">Trial window</p>
                <p className="mt-1 text-sm text-ink">
                  {data.tenant.trial_expires_at
                    ? `Expires ${formatDateTime(data.tenant.trial_expires_at)}`
                    : "No trial expiry set."}
                </p>
              </div>
              <label className="block text-sm font-medium text-ink">
                Change status
                <select className="mt-2 block w-full rounded-md border border-line bg-field px-3 py-2" value={status} onChange={(event) => setStatus(event.target.value as TenantStatus)}>
                  <option value="">Keep current</option>
                  {STATUSES.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </label>
              <label className="block text-sm font-medium text-ink">
                Change plan
                <select className="mt-2 block w-full rounded-md border border-line bg-field px-3 py-2" value={plan} onChange={(event) => setPlan(event.target.value as TenantPlan)}>
                  <option value="">Keep current</option>
                  {PLANS.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </label>
              <Button fullWidth isLoading={saving} onClick={() => void updateTenant()}>
                Apply controls
              </Button>
            </div>
          </AdminPanel>

          <AdminPanel title="Trial controls">
            <div className="space-y-4">
              <p className="text-sm leading-6 text-graphite/70">
                Trial workspaces run on enterprise access and can be converted later without changing tenant identity.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button isLoading={saving} onClick={() => void extendTrial(14)} variant="secondary">
                  Extend 14 days
                </Button>
                <Button isLoading={saving} onClick={() => void extendTrial(30)} variant="secondary">
                  Extend 30 days
                </Button>
              </div>
              <label className="block text-sm font-medium text-ink">
                Convert to plan
                <select className="mt-2 block w-full rounded-md border border-line bg-field px-3 py-2" value={convertPlan} onChange={(event) => setConvertPlan(event.target.value as TenantPlan)}>
                  {PLANS.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </label>
              <Button fullWidth isLoading={saving} onClick={() => void convertTrial()}>
                Convert trial
              </Button>
            </div>
          </AdminPanel>

          <AdminPanel title="Owner">
            <p className="text-sm font-medium text-ink">{data.tenant.profiles?.full_name || "No owner"}</p>
            <p className="mt-1 text-sm text-graphite/65">{data.tenant.profiles?.email ?? "Owner has not been assigned."}</p>
          </AdminPanel>

          <AdminPanel title="Danger zone">
            <p className="text-sm leading-6 text-graphite/70">
              Remove test tenants only. This deletes tenant-owned records and keeps the platform tenant protected.
            </p>
            <Button className="mt-4" isLoading={saving} onClick={() => void deleteTenant()} variant="danger">
              Delete tenant
            </Button>
          </AdminPanel>

          <AdminPanel title="Feature flags">
            <div className="space-y-2 text-sm">
              {data.featureFlags.length ? data.featureFlags.map((flag) => (
                <div className="flex items-center justify-between" key={flag.id}>
                  <span>{flag.key}</span>
                  <Badge tone={flag.enabled ? "success" : "neutral"}>{flag.enabled ? "on" : "off"}</Badge>
                </div>
              )) : <p className="text-graphite/65">No tenant-specific flags set.</p>}
            </div>
          </AdminPanel>
        </div>
      </div>
    </div>
  );
}
